import { Callout, Stack, FormField, Button, Inline, styled, css, TextInput } from 'canoe';
import React, { ChangeEvent, useRef, useState } from 'react';

import { RadiusProperties } from './types';
import { parseRadiusValue, renderRadiusProperties, wrapPixelValue } from './utils';

import { Input } from '../Input';

interface ShadowPickerProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Container = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.White};
    box-shadow: ${theme.boxShadow.default()};
    display: flex;
    flex-direction: column;
    padding: ${theme.spaces.normal};
    border-radius: ${theme.borderRadius.regular};
  `}
`;

export const RadiusPicker = React.memo(({ value: radiusText, onChange }: ShadowPickerProps) => {
  const radius = parseRadiusValue(radiusText);
  const [textInputValue, setTextInputValue] = useState<string>(radiusText);
  const updateRadius = (update: Partial<RadiusProperties>) => {
    const newValue = renderRadiusProperties({ ...radius, ...update });
    onChange(newValue);
    setTextInputValue(newValue);
  };
  const containerRef = useRef<HTMLDivElement>(null);

  const [pickerVisible, setPickerVisible] = useState(false);

  const onClear = () =>
    updateRadius({
      borderTopRight: '0px',
      borderTopLeft: '0px',
      borderBottomRight: '0px',
      borderBottomLeft: '0px',
    });

  const onUpdateText = (e: ChangeEvent<HTMLInputElement>) => {
    setTextInputValue(e.target.value);
    onChange(e.target.value);
  };

  return (
    <div ref={containerRef}>
      <Inline>
        <TextInput value={textInputValue} onChange={onUpdateText} />
        <Button
          appearance="outline"
          variant="contrast"
          onClick={() => window.requestAnimationFrame(() => setPickerVisible(true))}
        >
          Advanced
        </Button>
      </Inline>
      {pickerVisible && (
        <Callout
          usePortal={false}
          referenceElement={containerRef.current}
          onClose={() => setPickerVisible(false)}
          closeKey="Escape"
          placement="top-end"
        >
          {(calloutProps) => (
            <div
              ref={calloutProps.ref}
              style={{ ...calloutProps.style, zIndex: 1 }}
              {...calloutProps.attributes.popper}
            >
              <Container>
                <Stack>
                  <Inline>
                    <FormField label="Top Left" scale="small">
                      <Input
                        type="unit"
                        onChange={(value: string) =>
                          updateRadius({ borderTopLeft: wrapPixelValue(value) })
                        }
                        value={radius.borderTopLeft}
                      />
                    </FormField>
                    <FormField label="Top Right" scale="small">
                      <Input
                        type="unit"
                        onChange={(value: string) =>
                          updateRadius({ borderTopRight: wrapPixelValue(value) })
                        }
                        value={radius.borderTopRight}
                      />
                    </FormField>
                  </Inline>
                  <Inline>
                    <FormField label="Bottom Left" scale="small">
                      <Input
                        type="unit"
                        onChange={(value: string) =>
                          updateRadius({ borderBottomLeft: wrapPixelValue(value) })
                        }
                        value={radius.borderBottomLeft}
                      />
                    </FormField>

                    <FormField label="Bottom Right" scale="small">
                      <Input
                        type="unit"
                        onChange={(value: string) =>
                          updateRadius({ borderBottomRight: wrapPixelValue(value) })
                        }
                        value={radius.borderBottomRight}
                      />
                    </FormField>
                  </Inline>
                  <Inline>
                    <Button appearance="text" onClick={onClear}>
                      Clear
                    </Button>
                    <Button appearance="text" onClick={() => setPickerVisible(false)}>
                      Close
                    </Button>
                  </Inline>
                </Stack>
              </Container>
            </div>
          )}
        </Callout>
      )}
    </div>
  );
});
